/**
 * Membership sign-up form — ONLINE-ONLY variant.
 *
 * Identical to assets/membership-signup.js (the in-store QR-code page's
 * script) in every way except one: the payload posted on submit includes
 * `signup_tag: "reg_membership_online"`, which the shared Worker
 * (worker/worker.js) validates against its own allow-list and uses to tag
 * the new customer "reg_membership_online" instead of "reg_membership".
 * Kept as a separate file (rather than a shared script with a data-*
 * config flag) so this page can never be affected by changes made to the
 * in-store page's script, and vice versa.
 *
 * Responsibilities:
 *  1. Populate the Country select + phone dial-code select from a static
 *     ISO-3166 / calling-code table (Shopify has no native "all countries"
 *     Liquid object any more — localization.available_countries is limited
 *     to the shop's configured markets, so we ship our own table).
 *  2. Wire up city autocomplete on the City field via Photon
 *     (https://photon.komoot.io — a free, keyless, OpenStreetMap-based
 *     search API), restricted to whichever country is currently selected.
 *     This is the closest equivalent to "Shopify's address city list" —
 *     Shopify itself has no native per-country city dataset.
 *  3. Validate + submit the form via fetch() to the App Proxy endpoint
 *     (see data-proxy-path), which performs the email/phone duplicate
 *     check and creates the customer (tags, birth date metafield,
 *     newsletter consent) using the Admin API on the server side.
 *  4. On success, send the customer straight to the passwordless sign-in
 *     step with their email pre-filled (login_hint) and tell Shopify to
 *     bring them back to the membership page after they enter the 6-digit
 *     code (return_to).
 */
(function () {
  "use strict";

  // [ISO-3166-1 alpha-2, English name, calling code]
  var COUNTRIES = [
    ["AF","Afghanistan","93"],["AL","Albania","355"],["DZ","Algeria","213"],["AD","Andorra","376"],
    ["AO","Angola","244"],["AG","Antigua & Barbuda","1268"],["AR","Argentina","54"],["AM","Armenia","374"],
    ["AU","Australia","61"],["AT","Austria","43"],["AZ","Azerbaijan","994"],["BS","Bahamas","1242"],
    ["BH","Bahrain","973"],["BD","Bangladesh","880"],["BB","Barbados","1246"],["BY","Belarus","375"],
    ["BE","Belgium","32"],["BZ","Belize","501"],["BJ","Benin","229"],["BT","Bhutan","975"],
    ["BO","Bolivia","591"],["BA","Bosnia & Herzegovina","387"],["BW","Botswana","267"],["BR","Brazil","55"],
    ["BN","Brunei","673"],["BG","Bulgaria","359"],["BF","Burkina Faso","226"],["BI","Burundi","257"],
    ["KH","Cambodia","855"],["CM","Cameroon","237"],["CA","Canada","1"],["CV","Cape Verde","238"],
    ["CF","Central African Republic","236"],["TD","Chad","235"],["CL","Chile","56"],["CN","China","86"],
    ["CO","Colombia","57"],["KM","Comoros","269"],["CR","Costa Rica","506"],["HR","Croatia","385"],
    ["CU","Cuba","53"],["CY","Cyprus","357"],["CZ","Czechia","420"],["CD","Congo - Kinshasa","243"],
    ["CG","Congo - Brazzaville","242"],["DK","Denmark","45"],["DJ","Djibouti","253"],["DM","Dominica","1767"],
    ["DO","Dominican Republic","1849"],["EC","Ecuador","593"],["EG","Egypt","20"],["SV","El Salvador","503"],
    ["GQ","Equatorial Guinea","240"],["ER","Eritrea","291"],["EE","Estonia","372"],["SZ","Eswatini","268"],
    ["ET","Ethiopia","251"],["FJ","Fiji","679"],["FI","Finland","358"],["FR","France","33"],
    ["GA","Gabon","241"],["GM","Gambia","220"],["GE","Georgia","995"],["DE","Germany","49"],
    ["GH","Ghana","233"],["GR","Greece","30"],["GD","Grenada","1473"],["GT","Guatemala","502"],
    ["GN","Guinea","224"],["GW","Guinea-Bissau","245"],["GY","Guyana","592"],["HT","Haiti","509"],
    ["HN","Honduras","504"],["HK","Hong Kong SAR","852"],["HU","Hungary","36"],["IS","Iceland","354"],
    ["IN","India","91"],["ID","Indonesia","62"],["IR","Iran","98"],["IQ","Iraq","964"],
    ["IE","Ireland","353"],["IL","Israel","972"],["IT","Italy","39"],["CI","Côte d'Ivoire","225"],
    ["JM","Jamaica","1876"],["JP","Japan","81"],["JO","Jordan","962"],["KZ","Kazakhstan","7"],
    ["KE","Kenya","254"],["KI","Kiribati","686"],["KW","Kuwait","965"],["KG","Kyrgyzstan","996"],
    ["LA","Laos","856"],["LV","Latvia","371"],["LB","Lebanon","961"],["LS","Lesotho","266"],
    ["LR","Liberia","231"],["LY","Libya","218"],["LI","Liechtenstein","423"],["LT","Lithuania","370"],
    ["LU","Luxembourg","352"],["MO","Macao SAR","853"],["MG","Madagascar","261"],["MW","Malawi","265"],
    ["MY","Malaysia","60"],["MV","Maldives","960"],["ML","Mali","223"],["MT","Malta","356"],
    ["MH","Marshall Islands","692"],["MR","Mauritania","222"],["MU","Mauritius","230"],["MX","Mexico","52"],
    ["FM","Micronesia","691"],["MD","Moldova","373"],["MC","Monaco","377"],["MN","Mongolia","976"],
    ["ME","Montenegro","382"],["MA","Morocco","212"],["MZ","Mozambique","258"],["MM","Myanmar (Burma)","95"],
    ["NA","Namibia","264"],["NR","Nauru","674"],["NP","Nepal","977"],["NL","Netherlands","31"],
    ["NZ","New Zealand","64"],["NI","Nicaragua","505"],["NE","Niger","227"],["NG","Nigeria","234"],
    ["MK","North Macedonia","389"],["NO","Norway","47"],["OM","Oman","968"],["PK","Pakistan","92"],
    ["PW","Palau","680"],["PS","Palestinian Territories","970"],["PA","Panama","507"],["PG","Papua New Guinea","675"],
    ["PY","Paraguay","595"],["PE","Peru","51"],["PH","Philippines","63"],["PL","Poland","48"],
    ["PT","Portugal","351"],["QA","Qatar","974"],["RO","Romania","40"],["RU","Russia","7"],
    ["RW","Rwanda","250"],["KN","St. Kitts & Nevis","1869"],["LC","St. Lucia","1758"],["VC","St. Vincent & Grenadines","1784"],
    ["WS","Samoa","685"],["SM","San Marino","378"],["ST","São Tomé & Príncipe","239"],["SA","Saudi Arabia","966"],
    ["SN","Senegal","221"],["RS","Serbia","381"],["SC","Seychelles","248"],["SL","Sierra Leone","232"],
    ["SG","Singapore","65"],["SK","Slovakia","421"],["SI","Slovenia","386"],["SB","Solomon Islands","677"],
    ["SO","Somalia","252"],["ZA","South Africa","27"],["KR","South Korea","82"],["SS","South Sudan","211"],
    ["ES","Spain","34"],["LK","Sri Lanka","94"],["SD","Sudan","249"],["SR","Suriname","597"],
    ["SE","Sweden","46"],["CH","Switzerland","41"],["SY","Syria","963"],["TW","Taiwan","886"],
    ["TJ","Tajikistan","992"],["TZ","Tanzania","255"],["TH","Thailand","66"],["TL","Timor-Leste","670"],
    ["TG","Togo","228"],["TO","Tonga","676"],["TT","Trinidad & Tobago","1868"],["TN","Tunisia","216"],
    ["TR","Türkiye","90"],["TM","Turkmenistan","993"],["TV","Tuvalu","688"],["UG","Uganda","256"],
    ["UA","Ukraine","380"],["AE","United Arab Emirates","971"],["GB","United Kingdom","44"],["US","United States","1"],
    ["UY","Uruguay","598"],["UZ","Uzbekistan","998"],["VU","Vanuatu","678"],["VA","Vatican City","379"],
    ["VE","Venezuela","58"],["VN","Vietnam","84"],["YE","Yemen","967"],["ZM","Zambia","260"],
    ["ZW","Zimbabwe","263"]
  ];

  function byIso(iso) {
    for (var i = 0; i < COUNTRIES.length; i++) {
      if (COUNTRIES[i][0] === iso) return COUNTRIES[i];
    }
    return null;
  }

  function init(form) {
    var formId = form.id;
    var countrySelect = form.querySelector('#' + formId + '-Country');
    var dialSelect = form.querySelector('#' + formId + '-PhoneDialCode');
    var cityInput = form.querySelector('#' + formId + '-City');
    var cityResults = form.querySelector('#' + formId + '-CityResults');
    var messageBox = document.getElementById(formId + '-message');
    var submitBtn = form.querySelector('#' + formId + '-Submit');
    var defaultCountry = (form.dataset.defaultCountry || 'NL').toUpperCase();
    var proxyPath = form.dataset.proxyPath || '/apps/membership/signup';
    var loginPath = form.dataset.loginPath || '/customer_authentication/login';
    // Hardcoded shopify.com account Profile link (see the Liquid) — an
    // ABSOLUTE, cross-domain URL, so it can NEVER be passed as `return_to`
    // on /customer_authentication/login (relative-only, "Bad request"
    // otherwise, confirmed live); visit it directly instead.
    //
    // login_hint is appended below on a best-effort basis — confirmed live
    // that visiting routes.account_url/account_login_url internally
    // redirects through Shopify's own shopify.com/authentication/.../login
    // OAuth-authorize hop, which only forwards Shopify's own known params
    // and silently drops anything else we add, login_hint included. That's
    // server-side New Customer Accounts behavior, not specific to which
    // URL we start from, so this hardcoded literal likely hits the same
    // wall — but it costs nothing to keep trying since /profile itself
    // failed for an unrelated reason last time (wrong domain). If it's
    // still dropped, customers just type their email once on Shopify's
    // hosted login screen. (login_hint DOES work for the love-link branch
    // below, which goes through the classic same-domain
    // /customer_authentication/login instead.)
    var membershipPage = form.dataset.membershipPage || '/account';
    var loveLink = form.dataset.loveLink || '';

    function withLoginHint(url, emailForHint) {
      if (!emailForHint) return url;
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      return url + separator + 'login_hint=' + encodeURIComponent(emailForHint);
    }

    // Strings come from the theme's locale files (see locales/en.default.json,
    // "membership" namespace) via the data-i18n attribute. Defaults below are
    // only a safety net if that attribute is missing or malformed.
    var i18n = Object.assign(
      {
        duplicateEmail: 'This email is already registered. Please log in instead.',
        duplicatePhone: 'This phone number is already registered. Please log in instead.',
        loginCta: 'Log in',
        genericError: 'Something went wrong. Please try again.',
        networkError: 'A network error occurred. Please try again.',
        invalidBirthDate: 'Please enter a valid birth date.',
        loginHintInvalidEmail: 'Please enter a valid email.',
        loginHintNotFound: 'We could not find an account for that email.'
      },
      parseJSON(form.dataset.i18n)
    );

    // Shopify's `json` filter HTML-escapes its output (so it's safe to sit
    // inside the data-i18n='...' attribute below) — e.g. an apostrophe
    // becomes "&#39;". The browser normally undoes that automatically while
    // parsing the attribute out of the HTML, but that didn't always happen
    // here in practice, so decode defensively before JSON.parse instead of
    // ever risking a literal "&#39;" showing up in a message on the page.
    function decodeHtmlEntities(str) {
      var el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    }

    function parseJSON(str) {
      if (!str) return {};
      try {
        return JSON.parse(decodeHtmlEntities(str));
      } catch (e) {
        return {};
      }
    }

    // --- Populate country + dial code selects -----------------------------
    var countryFrag = document.createDocumentFragment();
    var dialFrag = document.createDocumentFragment();

    COUNTRIES.forEach(function (c) {
      var iso = c[0], name = c[1], dial = c[2];

      var opt = document.createElement('option');
      opt.value = iso;
      opt.textContent = name;
      if (iso === defaultCountry) opt.selected = true;
      countryFrag.appendChild(opt);

      var dOpt = document.createElement('option');
      dOpt.value = dial;
      dOpt.dataset.iso = iso;
      dOpt.textContent = '+' + dial + ' (' + iso + ')';
      if (iso === defaultCountry) dOpt.selected = true;
      dialFrag.appendChild(dOpt);
    });

    if (countrySelect) countrySelect.appendChild(countryFrag);
    if (dialSelect) dialSelect.appendChild(dialFrag);

    // --- Birth date: typed "dd/mm/yyyy" text field + calendar icon --------
    // The calendar icon itself is decorative; the real click/tap target is
    // the invisible-but-real-sized <input type="date"> sitting on top of it
    // (see the Liquid + CSS). No JS is needed to open the native picker —
    // the browser does that on its own when that input is clicked/tapped.
    // What's left here: mask the typed text field, and keep the two fields
    // (typed text <-> native date input) in sync in both directions.
    (function initBirthDate() {
      var displayInput = form.querySelector('#' + formId + '-BirthDate');
      var isoInput = form.querySelector('#' + formId + '-BirthDateISO');
      var nativeInput = form.querySelector('#' + formId + '-BirthDatePicker');
      if (!displayInput || !isoInput || !nativeInput) return;

      // Auto-inserts "/" as the visitor types digits: "03032002" -> "03/03/2002".
      function maskDisplayValue(raw) {
        var digits = raw.replace(/\D/g, '').slice(0, 8);
        var day = digits.slice(0, 2);
        var month = digits.slice(2, 4);
        var year = digits.slice(4, 8);
        var out = day;
        if (month) out += '/' + month;
        if (year) out += '/' + year;
        return out;
      }

      // "dd/mm/yyyy" -> { value: "yyyy-mm-dd" } if it's a real, non-future
      // date, otherwise { error: true }. Also rejects things like 31/02.
      function parseDisplayValue(text) {
        var match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim());
        if (!match) return { error: true };
        var day = Number(match[1]);
        var month = Number(match[2]);
        var year = Number(match[3]);
        var asDate = new Date(year, month - 1, day);
        var isRealDate =
          asDate.getFullYear() === year &&
          asDate.getMonth() === month - 1 &&
          asDate.getDate() === day;
        var isNotFuture = asDate <= new Date();
        if (!isRealDate || !isNotFuture) return { error: true };
        return { value: year + '-' + pad2(month) + '-' + pad2(day) };
      }

      displayInput.addEventListener('input', function () {
        var caretWasAtEnd = displayInput.selectionEnd === displayInput.value.length;
        displayInput.value = maskDisplayValue(displayInput.value);
        if (caretWasAtEnd) {
          displayInput.setSelectionRange(displayInput.value.length, displayInput.value.length);
        }
        isoInput.value = '';
      });

      // Right before the native picker opens (focus fires on click/tap,
      // ahead of the picker UI showing), seed it from whatever's already
      // typed so it starts on that date instead of today.
      var nativeFocusedAt = 0;
      nativeInput.addEventListener('focus', function () {
        nativeFocusedAt = Date.now();
        var parsed = parseDisplayValue(displayInput.value);
        if (!parsed.error) nativeInput.value = parsed.value;
      });

      nativeInput.addEventListener('change', function () {
        if (!nativeInput.value) return;

        // iOS Safari quirk: opening the wheel picker on a field that has
        // no value yet defaults the wheels to today's date, and merely
        // opening (or opening then dismissing without scrolling anything)
        // can fire this "change" with today's date already in it — before
        // the visitor picked anything. A change landing this fast after
        // focus, and worth exactly today, is that quirk firing rather than
        // a real pick (nobody spends under ~0.4s scrolling to a date, and
        // picking today's own date here would be an implausible birth
        // date), so treat it as a no-op instead of filling the field.
        var firedSuspiciouslyFast = Date.now() - nativeFocusedAt < 400;
        var todayIso = new Date().toISOString().slice(0, 10);
        if (firedSuspiciouslyFast && nativeInput.value === todayIso) {
          nativeInput.value = '';
          return;
        }

        var parts = nativeInput.value.split('-'); // "yyyy-mm-dd"
        displayInput.value = parts[2] + '/' + parts[1] + '/' + parts[0];
        isoInput.value = nativeInput.value;
      });
    })();

    // --- "Already have an account?" -> popup email lookup -> correct login -
    // See the comment above .membership-join__login-hint in the Liquid for
    // why this needs a lookup instead of being a plain link.
    (function initLoginHint() {
      var toggle = form.querySelector('[data-login-hint-toggle]');
      var modal = form.querySelector('[data-login-hint-modal]');
      if (!toggle || !modal) return;

      var closeBtn = modal.querySelector('[data-login-hint-close]');
      var emailInput = modal.querySelector('[data-login-hint-email]');
      var submitBtn = modal.querySelector('[data-login-hint-submit]');
      var messageEl = modal.querySelector('[data-login-hint-message]');
      if (!emailInput || !submitBtn) return;

      function showMessage(text) {
        if (!messageEl) return;
        messageEl.textContent = text;
        messageEl.hidden = !text;
      }

      function openModal() {
        showMessage('');
        emailInput.value = '';
        submitBtn.disabled = false;
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          // Very old browsers without <dialog> support: fall back to a
          // plain login rather than showing a popup that can't open.
          window.location.href = loginPath;
          return;
        }
        emailInput.focus();
      }

      // toggle is a <span role="button">, not a real button/link, so it
      // needs both click (mouse/touch) and Enter/Space (keyboard) handling.
      toggle.addEventListener('click', openModal);
      toggle.addEventListener('keydown', function (evt) {
        if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') {
          evt.preventDefault();
          openModal();
        }
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          modal.close();
        });
      }

      // Clicking the ::backdrop (i.e. anywhere outside the dialog's own
      // box) lands a click on the <dialog> element itself, not on any
      // content inside it — that's how native <dialog> "click outside to
      // dismiss" is normally detected.
      modal.addEventListener('click', function (evt) {
        if (evt.target === modal) modal.close();
      });

      function goToLogin(lookupEmail, needsLoveLink) {
        if (needsLoveLink && loveLink) {
          // Relative, same-domain path — still fine as a return_to value.
          window.location.href =
            loginPath + '?login_hint=' + encodeURIComponent(lookupEmail) + '&return_to=' + encodeURIComponent(loveLink);
          return;
        }
        // membershipPage is an absolute shopify.com URL — go there
        // directly instead of via return_to (relative-only).
        window.location.href = withLoginHint(membershipPage, lookupEmail);
      }

      function submitLookup() {
        var lookupEmail = emailInput.value.trim();
        if (!lookupEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lookupEmail)) {
          showMessage(i18n.loginHintInvalidEmail);
          return;
        }

        showMessage('');
        submitBtn.disabled = true;

        fetch(proxyPath, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ action: 'lookup', email: lookupEmail })
        })
          .then(function (res) {
            return res.json().catch(function () { return {}; });
          })
          .then(function (data) {
            if (!data || !data.ok) {
              // Lookup itself failed (not "email not found") — don't block
              // the visitor over a feature that's just about which bonus
              // link they land on. Fall back to a plain login.
              goToLogin(lookupEmail, false);
              return;
            }
            if (!data.exists) {
              submitBtn.disabled = false;
              showMessage(i18n.loginHintNotFound);
              return;
            }
            goToLogin(lookupEmail, !!data.needs_love_link);
          })
          .catch(function () {
            goToLogin(lookupEmail, false);
          });
      }

      submitBtn.addEventListener('click', submitLookup);
      emailInput.addEventListener('keydown', function (evt) {
        if (evt.key === 'Enter') {
          evt.preventDefault();
          submitLookup();
        }
      });
    })();

    // Keep the phone dial code in sync with the Country field.
    if (countrySelect && dialSelect) {
      countrySelect.addEventListener('change', function () {
        var match = byIso(countrySelect.value);
        if (match) dialSelect.value = match[2];
      });
    }

    // --- City autocomplete (Photon — free, no API key required) -----------
    if (cityInput && cityResults) {
      var PHOTON_ENDPOINT = 'https://photon.komoot.io/api/';
      var PLACE_TYPES = {
        city: true,
        town: true,
        village: true,
        hamlet: true,
        municipality: true,
        borough: true,
        suburb: true,
        quarter: true,
        state: false
      };

      var debounceTimer = null;
      var activeController = null;
      var activeIndex = -1;
      var currentItems = [];

      function closeResults() {
        cityResults.hidden = true;
        cityResults.innerHTML = '';
        cityInput.setAttribute('aria-expanded', 'false');
        activeIndex = -1;
        currentItems = [];
      }

      function labelFor(props) {
        var parts = [props.name];
        if (props.state && props.state !== props.name) parts.push(props.state);
        if (props.country) parts.push(props.country);
        return parts.join(', ');
      }

      function renderResults(features) {
        cityResults.innerHTML = '';
        currentItems = features;
        activeIndex = -1;

        if (!features.length) {
          closeResults();
          return;
        }

        features.forEach(function (feature, index) {
          var props = feature.properties || {};
          var li = document.createElement('li');
          li.setAttribute('role', 'option');
          li.id = formId + '-CityResult-' + index;
          li.tabIndex = -1;
          li.textContent = labelFor(props);
          li.addEventListener('mousedown', function (evt) {
            // mousedown (not click) so this fires before the input's blur handler.
            evt.preventDefault();
            selectResult(props);
          });
          cityResults.appendChild(li);
        });

        cityResults.hidden = false;
        cityInput.setAttribute('aria-expanded', 'true');
      }

      function selectResult(props) {
        cityInput.value = props.name || cityInput.value;
        closeResults();
      }

      function setActiveIndex(newIndex) {
        var options = cityResults.querySelectorAll('[role="option"]');
        if (!options.length) return;
        activeIndex = (newIndex + options.length) % options.length;
        options.forEach(function (opt, i) {
          opt.setAttribute('aria-selected', String(i === activeIndex));
        });
        cityInput.setAttribute('aria-activedescendant', options[activeIndex].id);
        options[activeIndex].scrollIntoView({ block: 'nearest' });
      }

      function fetchCities(query) {
        if (activeController) activeController.abort();
        activeController = new AbortController();

        var params = new URLSearchParams({ q: query, limit: '10', lang: 'en' });
        fetch(PHOTON_ENDPOINT + '?' + params.toString(), { signal: activeController.signal })
          .then(function (res) { return res.json(); })
          .then(function (data) {
            var features = (data && data.features) || [];
            var countryIso = countrySelect ? countrySelect.value : '';

            features = features.filter(function (f) {
              var props = f.properties || {};
              if (props.osm_key !== 'place') return false;
              if (!PLACE_TYPES[props.osm_value]) return false;
              if (countryIso && props.countrycode && props.countrycode.toUpperCase() !== countryIso) {
                return false;
              }
              return true;
            });

            // De-duplicate identical city/state/country combinations.
            var seen = {};
            features = features.filter(function (f) {
              var key = labelFor(f.properties || {});
              if (seen[key]) return false;
              seen[key] = true;
              return true;
            });

            renderResults(features.slice(0, 8));
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            closeResults();
          });
      }

      cityInput.addEventListener('input', function () {
        var query = cityInput.value.trim();
        clearTimeout(debounceTimer);
        if (query.length < 2) {
          closeResults();
          return;
        }
        debounceTimer = setTimeout(function () {
          fetchCities(query);
        }, 300);
      });

      cityInput.addEventListener('keydown', function (evt) {
        if (cityResults.hidden) return;
        if (evt.key === 'ArrowDown') {
          evt.preventDefault();
          setActiveIndex(activeIndex + 1);
        } else if (evt.key === 'ArrowUp') {
          evt.preventDefault();
          setActiveIndex(activeIndex - 1);
        } else if (evt.key === 'Enter') {
          if (activeIndex >= 0 && currentItems[activeIndex]) {
            evt.preventDefault();
            selectResult(currentItems[activeIndex].properties || {});
          }
        } else if (evt.key === 'Escape') {
          closeResults();
        }
      });

      cityInput.addEventListener('blur', function () {
        // Slight delay so a mousedown-selected option (see above) still registers.
        setTimeout(closeResults, 100);
      });

      document.addEventListener('click', function (evt) {
        if (!form.contains(evt.target)) closeResults();
      });
    }

    // --- Helpers --------------------------------------------------------------
    function showMessage(text, tone) {
      if (!messageBox) return;
      messageBox.textContent = text;
      messageBox.hidden = false;
      messageBox.classList.toggle('membership-join__message--ok', tone === 'ok');
    }

    function hideMessage() {
      if (messageBox) messageBox.hidden = true;
    }

    function toE164(dialCode, rawNumber) {
      var digits = (rawNumber || '').replace(/\D/g, '');
      digits = digits.replace(/^0+/, ''); // drop a leading trunk 0, e.g. "06..." -> "6..."
      return '+' + dialCode + digits;
    }

    function pad2(value) {
      return String(value).padStart(2, '0');
    }

    // Reads the birth date typed/picked in the field above. The hidden
    // #...-BirthDateISO input already holds "YYYY-MM-DD" if the visitor
    // used the calendar picker (see initBirthDate above) — but if they
    // typed "dd/mm/yyyy" by hand instead, that hidden field was cleared on
    // the last keystroke, so re-parse the visible text here as the source
    // of truth. Returns { value: string } on success or { error: true }.
    function readBirthDate() {
      var displayInput = form.querySelector('#' + formId + '-BirthDate');
      if (!displayInput) return { error: true };

      var match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(displayInput.value.trim());
      if (!match) return { error: true };
      var day = Number(match[1]);
      var month = Number(match[2]);
      var year = Number(match[3]);

      var asDate = new Date(year, month - 1, day);
      var isRealDate =
        asDate.getFullYear() === year &&
        asDate.getMonth() === month - 1 &&
        asDate.getDate() === day;
      var isNotFuture = asDate <= new Date();

      if (!isRealDate || !isNotFuture) return { error: true };
      return { value: year + '-' + pad2(month) + '-' + pad2(day) };
    }

    // --- Submit -----------------------------------------------------------
    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      hideMessage();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var birthDate = readBirthDate();
      if (birthDate.error) {
        showMessage(i18n.invalidBirthDate, 'error');
        return;
      }

      var email = form.querySelector('[name="email"]').value.trim();
      var phone = toE164(dialSelect.value, form.querySelector('[name="phone_number"]').value);
      var countryIso = countrySelect ? countrySelect.value : '';
      var countryMeta = byIso(countryIso);

      var payload = {
        first_name: form.querySelector('[name="first_name"]').value.trim(),
        last_name: form.querySelector('[name="last_name"]').value.trim(),
        email: email,
        phone: phone,
        country_code: countryIso,
        country_name: countryMeta ? countryMeta[1] : '',
        city: cityInput ? cityInput.value.trim() : '',
        birth_date: birthDate.value,
        newsletter: form.querySelector('[name="newsletter"]').checked,
        terms_accepted: form.querySelector('[name="terms"]').checked,
        // Tells the Worker to tag this customer "reg_membership_online"
        // instead of "reg_membership" — the one functional difference
        // from the in-store page's payload. Must match one of the values
        // in worker.js's ALLOWED_MEMBERSHIP_TAGS, or it's silently ignored
        // (falls back to the default tag) — see resolveMembershipTag there.
        signup_tag: 'reg_membership_online'
      };

      submitBtn.disabled = true;

      fetch(proxyPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (data) {
            return { status: res.status, data: data };
          });
        })
        .then(function (result) {
          var data = result.data || {};

          if (result.status === 200 && data.ok) {
            // New signups always go straight to the account (membershipPage
            // is an absolute shopify.com URL — can't go via
            // /customer_authentication/login?return_to=, relative-only).
            // Visiting it directly still routes through sign-in first since
            // they aren't authenticated yet.
            window.location.href = withLoginHint(membershipPage, email);
            return;
          }

          if (data.code === 'duplicate_email' || data.code === 'duplicate_phone') {
            // Existing customer hit the form by mistake. Only send them to
            // the love-link if the Worker says their account predates
            // AUTO_ENROLL_CUTOFF (i.e. they never got the 100-point bonus
            // automatically) — everyone created after that date already has
            // it, so they just go back to the regular membership page like
            // before, same as a fresh signup would.
            // Shopify only ever logs people in by email, so the email that
            // gets pre-filled here has to be an email that's actually on an
            // account. Usually that's just what they typed — but if the
            // MATCH was on phone number only (data.code === 'duplicate_phone'
            // and the typed email itself isn't registered to anyone), the
            // Worker hands back that other account's real email instead
            // (data.login_email), and that's what has to be used here.
            var loginEmail = data.login_email || email;
            var loginUrl;
            if (data.needs_love_link && loveLink) {
              // Relative, same-domain path — still fine as a return_to value.
              loginUrl = loginPath + '?login_hint=' + encodeURIComponent(loginEmail) + '&return_to=' + encodeURIComponent(loveLink);
            } else {
              // membershipPage is an absolute shopify.com URL — go there
              // directly instead of via return_to (relative-only).
              loginUrl = withLoginHint(membershipPage, loginEmail);
            }
            var fallback = data.code === 'duplicate_email' ? i18n.duplicateEmail : i18n.duplicatePhone;
            if (messageBox) {
              messageBox.innerHTML = (data.message || fallback) +
                ' <a href="' + loginUrl + '" style="text-decoration:underline;">' +
                i18n.loginCta + '</a>';
              messageBox.hidden = false;
            }
            submitBtn.disabled = false;
            return;
          }

          showMessage(data.message || i18n.genericError, 'error');
          submitBtn.disabled = false;
        })
        .catch(function () {
          showMessage(i18n.networkError, 'error');
          submitBtn.disabled = false;
        });
    });
  }

  function boot() {
    var forms = document.querySelectorAll('[data-membership-join-form]');
    forms.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();